import React from "react";

import type { LaneRendererProps } from "../lane.types";
import { GLASS_SLATE_SECONDARY_TEXT_CLASS, compressStateChips } from "../product/rendering";
import type { TimelineCrossLaneCoupling } from "../sync/sync.types";
import { selectCrossLaneCouplingsForTarget, selectCrossLaneCouplingsFromMetadata } from "../sync/sync.selectors";

function readCouplingDisplayLabel(
  coupling: TimelineCrossLaneCoupling,
  childLaneTitleById: Map<string, string>,
): string {
  if (coupling.sourceId === "track.atomic_runs") return "Atomic Runs";
  if (coupling.sourceId === "runtime.npc") return "NPC Runtime";

  return childLaneTitleById.get(coupling.sourceId)
    ?? coupling.anchoredRangeLabel
    ?? coupling.sourceId.replace(/^lane\./, "").replace(/\./g, " ");
}

export default function CapsuleLaneRenderer({ lane, items, viewport }: LaneRendererProps): JSX.Element {
  const childLaneIds = "childLaneIds" in lane ? lane.childLaneIds : [];
  const laneMetadata = ((lane as { metadata?: Record<string, unknown> }).metadata ?? {}) as Record<string, unknown>;
  const summary = (laneMetadata.summary ?? null) as { itemCount?: number; warningCount?: number; invalidationCount?: number; awaitingCount?: number; rangeSec?: number; syncHealth?: string } | null;
  const childRelations = Array.isArray(laneMetadata.childRelations)
    ? laneMetadata.childRelations as Array<{ laneId?: string; title?: string; health?: string; stateLabel?: string; details?: string | null }>
    : [];
  const rangeSec = typeof summary?.rangeSec === "number" ? summary.rangeSec : Math.max(0, items.reduce((maxDuration, item) => Math.max(maxDuration, Math.max(0, item.endSec - item.startSec)), 0));
  const invalidationCount = typeof summary?.invalidationCount === "number" ? summary.invalidationCount : 0;
  const awaitingCount = typeof summary?.awaitingCount === "number" ? summary.awaitingCount : 0;
  const warningCount = typeof summary?.warningCount === "number" ? summary.warningCount : 0;
  const chipLabel = lane.chipLabel ?? `${childLaneIds.length} child lane${childLaneIds.length === 1 ? "" : "s"}`;
  const lodTier = viewport.lodTier;
  const showDetailChips = lodTier === "near" || lodTier === "micro";
  const showCompactChips = lodTier === "mid";
  const isFar = lodTier === "far";
  const relationChipLimit = showDetailChips ? childRelations.length : showCompactChips ? 2 : 1;
  const visibleChildRelations = childRelations.slice(0, relationChipLimit);
  const compressedRelationTail = Math.max(0, childRelations.length - visibleChildRelations.length);
  const childLaneTitleById = new Map(
    childRelations
      .filter((relation): relation is { laneId: string; title?: string } => typeof relation.laneId === "string")
      .map((relation) => [relation.laneId, relation.title ?? relation.laneId]),
  );
  const directContextCouplings = selectCrossLaneCouplingsForTarget(selectCrossLaneCouplingsFromMetadata(laneMetadata), lane.id)
    .filter((coupling) => coupling.kind !== "capsule_membership");
  const visibleContextCouplings = directContextCouplings.slice(0, showDetailChips ? 2 : 1);
  const compressedContextCouplingTail = Math.max(0, directContextCouplings.length - visibleContextCouplings.length);
  const statusSummary = showDetailChips
    ? invalidationCount > 0 && awaitingCount > 0
      ? `${invalidationCount} child invalidation${invalidationCount === 1 ? "" : "s"} require review. ${awaitingCount} child lane${awaitingCount === 1 ? " is" : "s are"} awaiting derived input.`
      : invalidationCount > 0
        ? `${invalidationCount} child invalidation${invalidationCount === 1 ? "" : "s"} require review.`
        : awaitingCount > 0
          ? `${awaitingCount} child lane${awaitingCount === 1 ? " is" : "s are"} awaiting derived input.`
          : "All child lanes are currently aligned."
    : showCompactChips
      ? `${childLaneIds.length} child lanes. ${invalidationCount} review. ${awaitingCount} awaiting.`
      : invalidationCount > 0 || awaitingCount > 0
        ? `${invalidationCount} review · ${awaitingCount} awaiting`
        : "aligned";
  const footerStats = showDetailChips
    ? [
      `${summary?.itemCount ?? items.length} items`,
      `${warningCount} warnings`,
      `${invalidationCount} invalidations`,
      `${awaitingCount} awaiting`,
    ]
    : showCompactChips
      ? [
        `${summary?.itemCount ?? items.length} items`,
        `${invalidationCount} invalidations`,
        `${awaitingCount} awaiting`,
      ]
      : [
        `${invalidationCount} review`,
        `${awaitingCount} awaiting`,
      ];
  const summaryChips = compressStateChips([
    chipLabel,
    summary?.syncHealth?.replace(/_/g, " ") ?? "aligned",
  ], lodTier);

  const healthTone = summary?.syncHealth === "broken"
    ? "border-rose-300/28 bg-rose-200/12 text-rose-100"
    : summary?.syncHealth === "warning"
      ? "border-amber-300/28 bg-amber-200/12 text-amber-100"
      : summary?.syncHealth === "review_required"
        ? "border-slate-200/20 bg-slate-200/10 text-slate-100"
        : "border-emerald-300/24 bg-emerald-200/10 text-emerald-100";

  return (
    <div data-lane-class="capsule" data-lane-id={lane.id} data-lane-items={items.length} className="relative overflow-hidden rounded-2xl border border-fuchsia-300/20 bg-[linear-gradient(135deg,rgba(244,114,182,0.12),rgba(91,33,182,0.10),rgba(2,6,23,0.16))] px-3 py-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
      <div className="absolute inset-x-0 top-0 h-10 bg-[linear-gradient(90deg,rgba(244,114,182,0.14),rgba(236,72,153,0.04),rgba(244,114,182,0.14))]" />
      <div className="relative flex min-h-[52px] items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            {summaryChips.visible[0] ? (
              <span className="rounded-full border border-white/14 bg-slate-950/54 px-2 py-0.5 text-[10px] font-semibold text-slate-100">
                {summaryChips.visible[0]}
              </span>
            ) : null}
            {summaryChips.visible[1] ? (
              <span className={["rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em]", healthTone].join(" ")}>
                {summaryChips.visible[1]}
              </span>
            ) : null}
            {summaryChips.hiddenCount > 0 ? (
              <span className="rounded-full border border-slate-200/20 bg-slate-200/10 px-2 py-0.5 text-[9px] font-semibold text-slate-100">
                +{summaryChips.hiddenCount} more
              </span>
            ) : null}
          </div>
          {!isFar ? (
            <div className="mt-2 text-[11px] font-semibold text-slate-100">
              {showDetailChips ? "Derived child lanes are grouped under a real invalidation contract." : "Operational child invalidation contract."}
            </div>
          ) : null}
          <div className={["mt-1 text-[10px]", GLASS_SLATE_SECONDARY_TEXT_CLASS].join(" ")}>
            {childLaneIds.length > 0
              ? `${childLaneIds.length} child lanes over ${rangeSec.toFixed(1)}s. ${statusSummary}`
              : "This capsule has no registered child lanes yet."}
          </div>
          {visibleChildRelations.length > 0 ? (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {visibleChildRelations.map((relation) => {
                const tone = relation.health === "orphaned"
                  ? "border-rose-300/26 bg-rose-200/12 text-rose-100"
                  : relation.health === "partially_invalid" || relation.health === "drift_detected"
                    ? "border-amber-300/26 bg-amber-200/12 text-amber-100"
                    : relation.health === "needs_review"
                      ? "border-slate-200/20 bg-slate-200/10 text-slate-100"
                      : "border-emerald-300/20 bg-emerald-200/10 text-emerald-100";

                return (
                  <span
                    key={relation.laneId ?? relation.title}
                    title={relation.details ?? undefined}
                    className={["rounded-full border px-2 py-0.5 text-[9px] font-semibold", tone].join(" ")}
                  >
                    {isFar
                      ? `${relation.title ?? relation.laneId ?? "child lane"}`
                      : `${relation.title ?? relation.laneId ?? "child lane"} · ${relation.stateLabel ?? (relation.health ?? "aligned").replace(/_/g, " ")}`}
                  </span>
                );
              })}
              {!showDetailChips && compressedRelationTail > 0 ? (
                <span className="rounded-full border border-slate-200/20 bg-slate-200/10 px-2 py-0.5 text-[9px] font-semibold text-slate-100">
                  +{compressedRelationTail} more
                </span>
              ) : null}
            </div>
          ) : null}
          {visibleContextCouplings.length > 0 ? (
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {!isFar ? (
                <span className="text-[9px] font-semibold uppercase tracking-[0.12em] text-slate-50/84 [text-shadow:0_0_12px_rgba(255,255,255,0.14)]">
                  Context
                </span>
              ) : null}
              {visibleContextCouplings.map((coupling) => {
                const tone = coupling.state === "degraded"
                  ? "border-rose-300/26 bg-rose-200/12 text-rose-100"
                  : coupling.state === "awaiting_input"
                    ? "border-slate-200/20 bg-slate-200/10 text-slate-100"
                    : "border-cyan-300/24 bg-cyan-200/10 text-cyan-100";
                const label = readCouplingDisplayLabel(coupling, childLaneTitleById);

                return (
                  <span
                    key={coupling.id}
                    title={coupling.details}
                    className={["rounded-full border px-2 py-0.5 text-[9px] font-semibold", tone].join(" ")}
                  >
                    {isFar ? label : `${label} · ${coupling.state.replace(/_/g, " ")}`}
                  </span>
                );
              })}
              {compressedContextCouplingTail > 0 ? (
                <span className="rounded-full border border-slate-200/20 bg-slate-200/10 px-2 py-0.5 text-[9px] font-semibold text-slate-100">
                  +{compressedContextCouplingTail} more
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className="flex shrink-0 items-end gap-1.5">
          {Array.from({ length: Math.max(showDetailChips ? 3 : 2, Math.min(showDetailChips ? 7 : isFar ? 3 : 4, childLaneIds.length || 4)) }, (_, index) => {
            const relation = childRelations[index];
            const barClassName = relation?.health === "orphaned"
              ? "border-rose-200/18 bg-rose-200/30"
              : relation?.health === "partially_invalid" || relation?.health === "drift_detected"
                ? "border-amber-200/18 bg-amber-200/30"
                : relation?.health === "needs_review"
                  ? "border-slate-200/18 bg-slate-200/24"
                  : "border-emerald-200/16 bg-emerald-200/24";

            return (
              <div
                key={index}
                className={["w-3 rounded-full border", barClassName].join(" ")}
                style={{ height: `${18 + (((index % 4) + 1) * 7)}px` }}
              />
            );
          })}
        </div>
      </div>
      <div className="relative mt-2 flex items-center gap-2 text-[10px] text-slate-200/74">
        {footerStats.map((stat) => (
          <span key={stat}>{stat}</span>
        ))}
      </div>
    </div>
  );
}